import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiHome,
  FiPackage,
  FiUsers,
  FiDollarSign,
  FiBarChart2,
  FiSettings,
  FiMenu,
  FiX,
  FiLogOut,
  FiUser,
  FiCoffee,
} from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";

const VendorLayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f8f9fa;
  position: relative;

  @media (min-width: 1024px) {
    min-height: calc(100vh - 80px);
    margin-top: 80px;
  }
`;

const Sidebar = styled(motion.div)`
  width: 280px;
  background: white;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100vh;
  top: 0;
  left: ${(props) => (props.$isOpen ? "0" : "-280px")};
  transition: left 0.3s ease;
  z-index: 1001;

  @media (min-width: 1024px) {
    height: calc(100vh - 80px);
    top: 80px;
    position: fixed;
    left: 0 !important;
  }
`;

const SidebarHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 1rem;

  .logo {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: linear-gradient(135deg, #2d5016, #4a7c59);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 1.25rem;
  }

  h1 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #2d5016;
    margin: 0;
    flex: 1;
  }

  .mobile-menu-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid #e0e0e0;
    background: white;
    color: #666;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      border-color: #2d5016;
      color: #2d5016;
    }

    @media (min-width: 1024px) {
      display: none;
    }
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  padding: 1rem 0;
  overflow-y: auto;
`;

const MenuItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0 1rem;
`;

const MenuItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  color: #666;
  text-decoration: none;
  transition: all 0.3s ease;
  font-weight: 500;

  &:hover {
    background: #f8f9fa;
    color: #2d5016;
  }

  &.active {
    background: #2d5016;
    color: white;
  }

  .icon {
    font-size: 1.125rem;
  }
`;

const UserSection = styled.div`
  padding: 1rem;
  border-top: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .user-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2d5016, #4a7c59);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 0.875rem;
    font-weight: 600;
  }

  .user-info {
    flex: 1;

    .user-name {
      font-size: 0.875rem;
      font-weight: 600;
      color: #2d5016;
      margin: 0;
    }

    .user-role {
      font-size: 0.75rem;
      color: #666;
      margin: 0;
    }
  }

  .logout-btn {
    width: 32px;
    height: 32px;
    border: 1px solid #e0e0e0;
    background: white;
    color: #666;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      border-color: #dc3545;
      color: #dc3545;
    }
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 0;
  width: 100%;

  @media (min-width: 1024px) {
    margin-left: 280px;
    width: calc(100% - 280px);
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 0;
  position: relative;
`;

const MobileMenuButton = styled.button`
  position: fixed;
  top: 1rem;
  left: 1rem;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #2d5016;
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  z-index: 1000;
  transition: all 0.3s ease;

  &:hover {
    background: #1a3a0e;
    transform: scale(1.05);
  }

  @media (min-width: 1024px) {
    display: none;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  cursor: pointer;

  @media (min-width: 1024px) {
    display: none;
  }
`;

const VendorLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  // Debug: Log sidebar state changes
  useEffect(() => {
    console.log("=== VENDOR SIDEBAR STATE ===");
    console.log("sidebarOpen:", sidebarOpen);
    console.log("isDesktop:", isDesktop);
  }, [sidebarOpen, isDesktop]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const menuItems = [
    {
      path: "/vendor",
      label: "Dashboard",
      icon: <FiHome />,
    },
    {
      path: "/vendor/meal-plans",
      label: "Meal Plans",
      icon: <FiPackage />,
    },
    {
      path: "/vendor/meals",
      label: "Standalone Meals",
      icon: <FiCoffee />,
    },
    {
      path: "/vendor/subscribers",
      label: "Subscribers",
      icon: <FiUsers />,
    },
    {
      path: "/vendor/orders",
      label: "Orders",
      icon: <FiDollarSign />,
    },
    {
      path: "/vendor/analytics",
      label: "Analytics",
      icon: <FiBarChart2 />,
    },
    {
      path: "/vendor/settings",
      label: "Settings",
      icon: <FiSettings />,
    },
  ];

  const handleLogout = () => {
    logout();
  };

  const closeSidebar = () => {
    console.log("Close sidebar called - current state:", sidebarOpen);
    setSidebarOpen(false);
  };

  const toggleSidebar = () => {
    console.log("Toggle sidebar - current state:", sidebarOpen);
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <VendorLayoutContainer>
      {sidebarOpen && (
        <Overlay
          onClick={(e) => {
            console.log("OVERLAY CLICKED!", e);
            closeSidebar();
          }}
        />
      )}

      <Sidebar
        $isOpen={sidebarOpen}
        initial={{ x: -280 }}
        animate={{
          x: isDesktop ? 0 : sidebarOpen ? 0 : -280,
        }}
        transition={{ duration: 0.3 }}
      >
        <SidebarHeader>
          <div className="logo">N</div>
          <h1>NourishGY</h1>
          <button className="mobile-menu-toggle" onClick={closeSidebar}>
            <FiX />
          </button>
        </SidebarHeader>

        <SidebarContent>
          <MenuItems>
            {menuItems.map((item) => (
              <MenuItem
                key={item.path}
                to={item.path}
                className={location.pathname === item.path ? "active" : ""}
                onClick={closeSidebar}
              >
                <span className="icon">{item.icon}</span>
                {item.label}
              </MenuItem>
            ))}
          </MenuItems>
        </SidebarContent>

        <UserSection>
          <div className="user-avatar">{user?.firstName?.charAt(0) || "V"}</div>
          <div className="user-info">
            <p className="user-name">{user?.firstName || "Vendor"}</p>
            <p className="user-role">Vendor</p>
          </div>
          <button className="logout-btn" onClick={handleLogout} title="Logout">
            <FiLogOut />
          </button>
        </UserSection>
      </Sidebar>

      <MainContent>
        <MobileMenuButton onClick={toggleSidebar}>
          <FiMenu />
        </MobileMenuButton>
        <ContentArea>{children}</ContentArea>
      </MainContent>
    </VendorLayoutContainer>
  );
};

export default VendorLayout;
